class PredictiveSearchDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-search-drawer]');
    if (!this.drawer) return;

    this.input = this.drawer.querySelector('[data-search-input]');
    this.clearButton = this.drawer.querySelector('[data-clear-search]');
    this.form = this.drawer.querySelector('[data-search-form]');
    this.resultsContainer = this.drawer.querySelector('[data-search-results]');
    this.endpoint = this.drawer.getAttribute('data-predictive-url');
    this.sectionId = this.drawer.getAttribute('data-section-id');
    this.abortController = null;

    this.debouncedSearch = this.debounce((term) => this.fetchResults(term), 300);
    this.init();
  }

  init() {
    if (this.input) {
      // Update clear button visibility based on input value
      const updateClearButton = () => {
        if (this.clearButton) {
          if (this.input.value.length > 0) {
            this.clearButton.classList.add('is-visible');
          } else {
            this.clearButton.classList.remove('is-visible');
          }
        }
      };

      // Initial state
      updateClearButton();

      this.input.addEventListener('input', (e) => {
        const term = e.target.value.trim();
        updateClearButton();
        
        if (!term.length) {
          this.clearResults();
          return;
        }
        this.updateStatus('loading');
        this.debouncedSearch(term);
      });

      // Prevent Enter key from submitting form - just trigger search
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const term = e.target.value.trim();
          if (term && term.length) {
            this.updateStatus('loading');
            this.debouncedSearch(term);
          }
        }
      });
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.input.focus();
        this.clearResults();
        // Hide clear button after clearing
        this.clearButton.classList.remove('is-visible');
      });
    }

    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const term = this.input?.value.trim();
        if (term && term.length) {
          // Navigate to search page with the term
          window.location.href = `${this.form.action}?q=${encodeURIComponent(term)}`;
        }
      });
    }

    this.drawer.addEventListener('drawer:open', () => {
      requestAnimationFrame(() => this.input?.focus());
    });

    // Register search triggers
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (typeof this.drawer.open === 'function') {
          this.drawer.open();
        }
        requestAnimationFrame(() => this.input?.focus());
      });
    });
  }

  updateStatus(state) {
    if (!this.resultsContainer) return;
    
    if (state === 'loading') {
      this.resultsContainer.classList.add('is-loading');
    } else {
      this.resultsContainer.classList.remove('is-loading');
    }
  }

  clearResults() {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
      this.resultsContainer.classList.remove('is-loading');
    }
  }

  async fetchResults(term) {
    if (!this.endpoint || !this.sectionId || !this.resultsContainer) {
      console.warn('Search drawer not properly initialized');
      return;
    }

    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Build URL with search parameters
    // routes.predictive_search_url returns a path like "/search/suggest" or "/en/search/suggest"
    // Construct URL - if endpoint is absolute, use it; otherwise prepend root
    let baseUrl;
    if (this.endpoint.startsWith('http')) {
      baseUrl = this.endpoint;
    } else {
      const root = window.Shopify?.routes?.root || '/';
      const endpointPath = this.endpoint.startsWith('/') ? this.endpoint : `/${this.endpoint}`;
      baseUrl = root.replace(/\/$/, '') + endpointPath;
    }
    
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      this.updateStatus('loading');

      const requestUrl = url.toString();
      console.log('Fetching search results from:', requestUrl);

      const response = await fetch(requestUrl, {
        headers: { Accept: 'text/html' },
        signal: this.abortController.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Search API error:', response.status, errorText);
        throw new Error(`Search failed: ${response.status}`);
      }

      const html = await response.text();
      console.log('Full HTML response:', html);
      
      const doc = new DOMParser().parseFromString(html, 'text/html');
      
      // Shopify wraps sections with id="shopify-section-{section_id}"
      // Example: section file "search-drawer-content.liquid" -> id="shopify-section-search-drawer-content"
      const sectionId = `shopify-section-${this.sectionId}`;
      const section = doc.querySelector(`#${sectionId}`);
      
      console.log('Looking for section:', sectionId);
      console.log('Section element:', section);
      
      if (section) {
        // According to Shopify docs, we should use section.innerHTML directly
        // The section's innerHTML contains our .search-drawer__results-fragment div
        const sectionContent = section.innerHTML.trim();
        
        console.log('Section found! Content length:', sectionContent.length);
        console.log('Full section HTML:', section.outerHTML);
        console.log('Section innerHTML:', section.innerHTML);
        
        if (sectionContent && sectionContent.length > 0) {
          this.resultsContainer.innerHTML = sectionContent;
          console.log('Results displayed successfully');
        } else {
          // Empty response - check if predictive_search.performed is false
          // The section should still render the else block with prompt
          console.warn('Section found but content is empty. This might mean predictive_search.performed is false or section has no content.');
          
          // Try to render the section wrapper itself as fallback
          const sectionOuter = section.outerHTML;
          if (sectionOuter && sectionOuter.length > sectionContent.length) {
            console.log('Trying to use section outerHTML instead');
            // Extract just the inner content from outerHTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sectionOuter;
            const innerContent = tempDiv.querySelector(`#${sectionId}`)?.innerHTML.trim();
            if (innerContent) {
              this.resultsContainer.innerHTML = innerContent;
              this.updateStatus('idle');
              return;
            }
          }
          
          this.resultsContainer.innerHTML = '<p class="search-drawer__empty">No results found</p>';
        }
        this.updateStatus('idle');
      } else {
        // Section not found - log for debugging
        console.error('Section not found in response. Looking for:', sectionId);
        console.log('Full URL:', url.toString());
        console.log('Response HTML preview:', html.substring(0, 1000));
        const availableSections = Array.from(doc.querySelectorAll('[id^="shopify-section"]')).map(s => s.id);
        console.log('Available sections:', availableSections);
        
        // Try to find any section as fallback
        if (availableSections.length > 0) {
          const firstSection = doc.querySelector(`#${availableSections[0]}`);
          if (firstSection) {
            console.log('Using first available section as fallback');
            this.resultsContainer.innerHTML = firstSection.innerHTML.trim();
            this.updateStatus('idle');
            return;
          }
        }
        
        this.resultsContainer.innerHTML = '<p class="search-drawer__empty">Search unavailable</p>';
        this.updateStatus('idle');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Search error:', error);
      this.resultsContainer.innerHTML = '<p class="search-drawer__empty">Search error occurred</p>';
      this.updateStatus('idle');
    }
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PredictiveSearchDrawer());
} else {
  new PredictiveSearchDrawer();
}
